import { cn } from '@rocco/utils/cn';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useHydrated } from 'remix-utils';
import { useFixedUIHidden } from '~/modules/root';
// Imported from the leaf module rather than the island's barrel: this only needs the
// surface token, and the barrel would pull the whole island (and framer-motion) in.
import { ISLAND_GLASS_LIGHT } from '~/ui-rocco/pages/detail-shared/magic-island/styles';
import { EditModeToggle } from './EditModeToggle';
import { CopyLinkButton } from './CopyLinkButton';
import {
    VisibilityToggleButton,
    type VisibilityItemType,
} from './VisibilityToggleButton';
import { PinToDirectoryButton } from './PinToDirectoryButton';
import { StaffAvatarButton } from './StaffAvatarButton';
import makeSharableInternalLink from '../../util/shareable-internal-link';

interface DetailPageButtonsProps {
    /** The ID of the directory item. */
    id: number;
    /** The link to the edit page (Business Manager). */
    editLink: string;
    /** The shareable link (with tracking query params added). */
    shareableLink: string;
    /** The current user has permission to edit this item. */
    canEdit: boolean;
    /** The current user has permission to copy the shareable link. */
    canCopyLink: boolean;
    /** Whether to show the buttons. Defaults to `true`. */
    showButton?: boolean;
    /** When provided, renders a visibility toggle button for admin users. */
    visibilityToggle?: {
        isInDirectory: boolean;
        itemType: VisibilityItemType;
        onToggle: (inLibrary: boolean) => void;
        disabled?: boolean;
        /** Extra line appended to the remove-from-directory confirm copy. */
        removalNote?: string;
    };
    /** When provided, renders a pin-to-directory toggle button for admin users. */
    pinToggle?: {
        isPinned: boolean;
        isInDirectory: boolean;
        onToggle: (pinned: boolean) => void;
    };
}

/**
 * The "Copy share link" and "Switch to edit mode" buttons on directory item
 * detail pages (professional, product, project, article). Only visible if the
 * current user has the appropriate permissions.
 */
export const DetailPageButtons = ({
    id,
    editLink,
    shareableLink,
    canEdit,
    canCopyLink,
    showButton = true,
    visibilityToggle,
    pinToggle,
}: DetailPageButtonsProps) => {
    const isHydrated = useHydrated();
    const isFixedUIHidden = useFixedUIHidden();

    const { desktop } = useAppDisplayModeContext();
    if (!desktop) return null;

    // Every action is permission-gated, but the frame around them is not, so
    // this has to be checked here: a reader with none of them would otherwise
    // get the frosted pill on its own — a small empty circle on a public detail
    // page, and `pointer-events-auto` would have it swallow clicks. Before the
    // frame the empty container had no size and no surface, so it cost nothing.
    // The avatar does not count: it says who is holding the tools, and with no
    // tools there is nothing for it to introduce.
    const hasActions =
        canEdit || canCopyLink || !!pinToggle || !!visibilityToggle;
    if (!hasActions) return null;

    const internalLink = isHydrated
        ? makeSharableInternalLink(window.location.origin, shareableLink, id)
        : '';

    return (
        <div
            className={cn(
                // Sits in the same band as the Magic Island (bottom-8) and borrows its
                // frosted-glass surface, so the two read as one system rather than two
                // unrelated floating controls.
                ISLAND_GLASS_LIGHT,
                'fixed bottom-8 left-8 z-fixed flex flex-row items-center gap-2 rounded-tile p-2 transition-[opacity,visibility] duration-300',
                // The page's standard corner, on the frame and on everything
                // inside it. The controls are aria `Button`s whose circular
                // radius is set by Fela at a specificity plain utilities lose
                // to, so these have to be marked important to land.
                '[&_button]:rounded-tile! [&_img]:rounded-tile!',
                (!showButton || isFixedUIHidden) && 'invisible opacity-0',
            )}
        >
            <StaffAvatarButton />
            {pinToggle && <PinToDirectoryButton {...pinToggle} />}
            {visibilityToggle && (
                <VisibilityToggleButton {...visibilityToggle} />
            )}
            {canCopyLink && <CopyLinkButton url={internalLink} />}
            {canEdit && <EditModeToggle editLink={editLink} />}
        </div>
    );
};
